import { useState } from "react";
import { useEffect } from "react";
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Grid,
  DialogContent,
  DialogTitle,
  Dialog,
  DialogActions,
  Button,
} from "@mui/material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import download from "../../../../../../assets/images/download.svg";
import { useUserStore } from "../../../../../../store/useUserStore";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getDocument } from "../../../../../../services/dashboard";
import { useToast } from "../../../../../../hooks/useToast";

export default function ClaimScheduleForm(props: any) {
  const {
    receiptFile,
    iceClaimNumber,
    claimBenfitSchedule,
    handleCheckboxChange,
    isChecked,
    isAssigned,
  } = props;
  const role = useUserStore((state) => state.role);
  const [hasUserChanged, setHasUserChanged] = useState(false);
  const [isVerifiedValue, setIsVerifiedValue] = useState(
    claimBenfitSchedule?.isVerified || false
  );
  const [openPreview, setOpenPreview] = useState(false);
  const [selectedDoc, setSelectedDoc] = useState<any>(null);

  const {showInfoToast} = useToast()

  const handleClosePreview = () => {
    setOpenPreview(false);
    setSelectedDoc(null);
  };

  const handlePreview = async (doc: any) => {
    try {
      if (doc.isLocal) {
        setSelectedDoc(doc);
        setOpenPreview(true);
      } else {
        const res = await getDocument(iceClaimNumber, doc.document_id);
        if (!res || res.status !== 200) {
          showInfoToast("Something went wrong")
          throw new Error("Failed to fetch document");
        }

        const blob = res.data;
        const url = URL.createObjectURL(blob);

        setSelectedDoc({ ...doc, file_url: url });
        setOpenPreview(true);
      }
    } catch (err) {
      console.error("Error fetching file:", err);
    }
  };

  const handleDownload = async (doc: any) => {
    try {
      const res = await getDocument(iceClaimNumber, doc.document_id);
      if (!res || res.status !== 200) {
        throw new Error("Failed to download document");
      }

      const blob = res.data;
      const url = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = url;
      link.download = doc.file_name || "document.pdf";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error downloading file:", err);
    }
  };

  useEffect(() => {
    if (!hasUserChanged) {
      setIsVerifiedValue(!!claimBenfitSchedule?.isVerified);
    }
  }, [claimBenfitSchedule?.isVerified, hasUserChanged]);

  return (
    <Box p={4}>
      {/* Key-Value Rows */}
      <Box display="flex" flexDirection="column" gap={3}>
        <Row
          label="Policy creation date:"
          value={claimBenfitSchedule.policyCreationDate}
        />
        <Row
          label="Number of paid premium:"
          value={claimBenfitSchedule.numberOfPaidPremium}
        />
        <Row
          label="Total paid premium:"
          value={claimBenfitSchedule.totalPaidPremium}
        />
        <Row
          label="Total eligible amount:"
          value={claimBenfitSchedule.eligibilityAmountForTenPercent}
        />
        <Row
          label="10% Claim Payout:"
          value={claimBenfitSchedule.cashbackAmount}
          highlight
        />
      </Box>

      {/* Checkbox */}
      <Box mt={4} textAlign="left">
        <FormControlLabel
          control={
            <Checkbox
              checked={isChecked}
              onChange={(e) => {
                handleCheckboxChange?.(e); 
              }}
              disabled={
                !(
                  isAssigned &&
                  (role === "claim_administrator" || role === "claim_manager")
                )
              }
            />
          }
          label={
            <Typography fontSize={25} fontWeight={400}>
              I have verified above mentioned details in legacy system
            </Typography>
          }
        />
      </Box>
      <Grid container sx={{ mt: 3 }} spacing={5}>
        {role === "finance" &&
          receiptFile?.map((doc: any, index: number) => (
            <Grid size={{ xs: 12, md: 4 }}>
              <Box>
                <Typography
                  fontWeight={600}
                  textAlign="left"
                  fontSize={18}
                  mb={1}
                >
                  Reciept
                </Typography>
                <Box
                  key={index}
                  sx={{
                    background: "#F5EEE9",
                    color: "#FF0000",
                    borderRadius: 1,
                    px: 1.5,
                    py: 0.5,
                    display: "flex",
                    alignItems: "center",
                    mb: 1,
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} flexGrow={1}>
                    <PictureAsPdfIcon />
                    <Box>
                      <Typography fontSize={14}>{doc.file_name}</Typography>
                      <Typography
                        fontSize={12}
                        color="text.secondary"
                      ></Typography>
                    </Box>
                  </Box>
                  <IconButton
                    onClick={() => handlePreview(doc)}
                    size="small"
                  >
                    <VisibilityIcon sx={{ color: "#EC6E00" }} />
                  </IconButton>
                </Box>
              </Box>
            </Grid>
          ))}
      </Grid>
      <Dialog
        open={openPreview}
        onClose={handleClosePreview}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>Document Preview</DialogTitle>
        <DialogContent dividers>
          {selectedDoc ? (
            <object
              data={selectedDoc.file_url}
              type="application/pdf"
              width="100%"
              height="600px"
            >
              <p>
                PDF preview is not supported in this browser.{" "}
                <a
                  href={selectedDoc.file_url}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Download PDF
                </a>
              </p>
            </object>
          ) : (
            <Typography>Loading...</Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => handleDownload(selectedDoc)}
            color="primary"
            variant="outlined"
          >
            Download
          </Button>
          <Button
            onClick={handleClosePreview}
            color="secondary"
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function Row({
  label,
  value,
  highlight = false,
}: {
  label: string;
  value: string;
  highlight?: boolean;
}) {
  return (
    <Box display="flex" justifyContent="space-between" alignItems="center">
      <Typography
        fontWeight={600}
        color={highlight ? "#EC6E00" : "#000"}
        fontSize={25}
      >
        {label}
      </Typography>
      <Typography
        fontWeight={400}
        color={highlight ? "#EC6E00" : "#000"}
        fontSize={25}
      >
        {value}
      </Typography>
    </Box>
  );
}
